/**
 * CheckoutWizard — 3-step checkout stepper matching live MRT
 *
 * Steps:
 *   1. Shipping Information — contact details (pre-filled from user),
 *      ship-to address dropdown, weight (auto-calculated), delivery option,
 *      requested delivery date, order criticality
 *   2. Billing Information — reference number / PO (unique per customer),
 *      identification text, billing address dropdown
 *   3. Summary — delivery & invoice addresses, item details, checkout summary,
 *      T&C checkbox (must tick before submit)
 *
 * Post-submit: confirmation page "Thanks for shopping with us!" with
 * reference number echo, Continue shopping / View my order CTAs,
 * toast "Thanks for submitting your order".
 *
 * API values:
 *   Delivery option (shipment control): 10=Partial, 20=Ship Complete line, 30=Ship Complete Order
 *   Criticality: 10=Normal, 20=Stock-Replenishment order, 30=Unit down only
 *
 * T&C PDFs:
 *   /documents/tc-canada-en.pdf (English)
 *   /documents/tc-canada-fr.pdf (French)
 *   Production: managed via Content Management module per sales area + language.
 *
 * @module components/commerce/CheckoutWizard
 * @since 2026-05-25 (Group G)
 */
import { useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '@contexts/CartContext';
import { useAuth } from '@contexts/AuthContext';
import { DEMO_CUSTOMER, DEMO_ORDERS } from '@data/demoData';
import Toast from '@components/common/Toast';

// ── Dropdown options ──────────────────────────────────────────────

const DELIVERY_OPTIONS = [
  { value: '10', label: 'Partial' },
  { value: '20', label: 'Ship Complete line' },
  { value: '30', label: 'Ship Complete Order' },
];

const CRITICALITY_OPTIONS = [
  { value: '10', label: 'Normal' },
  { value: '20', label: 'Stock- Replenishment order' },
  { value: '30', label: 'Unit down only will ship if available today' },
];

// ── Helpers ───────────────────────────────────────────────────────

function formatCurrency(amount, currency = 'CAD') {
  if (amount == null || isNaN(amount)) return '—';
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(amount);
}

function getTodayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function splitName(fullName) {
  if (!fullName) return { first: '', last: '' };
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] };
}

function isPOUnique(poRef, customerId) {
  if (!poRef || !poRef.trim()) return true;
  const normalised = poRef.trim().toUpperCase();
  return !DEMO_ORDERS
    .filter(o => o.customerId === customerId)
    .some(o => (o.customerRef || '').toUpperCase() === normalised);
}

// ── Step indicator (chevron-style stepper bar) ────────────────────

function StepIndicator({ currentStep, steps }) {
  return (
    <div className="flex mb-8">
      {steps.map((step, i) => {
        const isActive = i === currentStep;
        const isCompleted = i < currentStep;
        return (
          <div
            key={step}
            className={`flex-1 relative flex items-center justify-center py-3 text-sm font-sandvik-text
              ${isActive
                ? 'bg-electric-blue text-white font-normal'
                : isCompleted
                  ? 'bg-eb-1 text-electric-blue font-normal'
                  : 'bg-n1 text-n5'}`}
          >
            <span className="mr-2 text-xs font-normal">
              {isCompleted ? '✓' : i + 1}
            </span>
            {step}
            {i < steps.length - 1 && (
              <div className="absolute right-0 top-0 bottom-0 w-4 z-10">
                <svg viewBox="0 0 16 48" className="h-full w-full" preserveAspectRatio="none">
                  <path
                    d="M0,0 L12,24 L0,48"
                    fill={isActive ? '#1441F5' : isCompleted ? '#C8DCFF' : '#F8F8F8'}
                    stroke="white"
                    strokeWidth="1"
                  />
                </svg>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Field wrapper ─────────────────────────────────────────────────

function Field({ label, required, children, hint, error }) {
  return (
    <div className="mb-4">
      <label className="block text-sm text-carbon-black font-normal mb-1">
        {required && <span className="text-red-500 mr-0.5">*</span>}
        {label}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-n5 mt-1">{hint}</p>}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

// ── T&C Modal ─────────────────────────────────────────────────────

function TCModal({ onClose }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center pt-12">
      <div className="absolute inset-0 bg-carbon-black/50" onClick={onClose} />
      <div className="relative bg-white w-full max-w-4xl max-h-[80vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-n2">
          <div>
            <h2 className="text-xl font-sandvik-headline text-carbon-black">
              Sandvik digital product terms of use
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/documents/tc-canada-en.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-carbon-black hover:text-electric-blue underline flex items-center gap-1"
            >
              Download PDF (EN)
              <span className="material-symbols-outlined text-sm">chevron_right</span>
            </a>
            <a
              href="/documents/tc-canada-fr.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-carbon-black hover:text-electric-blue underline flex items-center gap-1"
            >
              Download PDF (FR)
              <span className="material-symbols-outlined text-sm">chevron_right</span>
            </a>
            <button onClick={onClose} className="p-1 hover:bg-n1" aria-label="Close">
              <span className="material-symbols-outlined text-xl text-carbon-black">close</span>
            </button>
          </div>
        </div>
        {/* PDF embed */}
        <div className="flex-1 overflow-hidden">
          <iframe
            src="/documents/tc-canada-en.pdf"
            title="Terms and Conditions"
            className="w-full h-full min-h-[60vh]"
          />
        </div>
        {/* Footer */}
        <div className="px-6 py-3 border-t border-n2 flex justify-center">
          <button
            onClick={onClose}
            className="px-8 py-2 bg-n2 text-sm text-carbon-black hover:bg-n3 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Step 1: Shipping Information ──────────────────────────────────

function ShippingStep({ form, setField, totalWeight }) {
  return (
    <div>
      <h2 className="text-lg font-sandvik-headline text-carbon-black mb-4">
        Shipping information
      </h2>

      {/* Contact details */}
      <div className="mb-6">
        <h3 className="text-sm font-normal text-carbon-black mb-3 flex items-center gap-1">
          Contact details
          <span className="material-symbols-outlined text-sm text-n4 cursor-help" title="Pre-filled from your account. Edit if needed.">info</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Field label="First Name">
            <input
              type="text"
              value={form.contactFirstName}
              onChange={e => setField('contactFirstName', e.target.value)}
              className="w-full border border-n3 px-3 py-2.5 text-sm focus:ring-1 focus:ring-electric-blue focus:border-electric-blue"
            />
          </Field>
          <Field label="Last name">
            <input
              type="text"
              value={form.contactLastName}
              onChange={e => setField('contactLastName', e.target.value)}
              className="w-full border border-n3 px-3 py-2.5 text-sm focus:ring-1 focus:ring-electric-blue focus:border-electric-blue"
            />
          </Field>
        </div>
        <Field label="Phone Number">
          <input
            type="tel"
            value={form.contactPhone}
            onChange={e => setField('contactPhone', e.target.value)}
            className="w-full border border-n3 px-3 py-2.5 text-sm focus:ring-1 focus:ring-electric-blue focus:border-electric-blue"
          />
        </Field>
      </div>

      <div className="border-t border-n2 pt-4 mb-4" />

      <Field label="Ship to" required
        hint="Select a delivery address from your account. Contact your Sandvik representative to add new addresses.">
        <select
          value={form.shipToId}
          onChange={e => setField('shipToId', e.target.value)}
          className="w-full border border-n3 px-3 py-2.5 text-sm focus:ring-1 focus:ring-electric-blue focus:border-electric-blue"
        >
          <option value="">— Select delivery address —</option>
          {DEMO_CUSTOMER.shippingAddresses.map(addr => (
            <option key={addr.id} value={addr.id}>{addr.label}</option>
          ))}
        </select>
      </Field>

      <Field label="Approximate weight of this order">
        <div className="flex items-center gap-2">
          <div className="w-40 border border-n2 bg-n1 px-3 py-2.5 text-sm text-carbon-black">
            {totalWeight.toFixed(1)}
          </div>
          <span className="text-sm text-n5">lb</span>
        </div>
        <p className="text-xs text-n5 mt-1">Auto-calculated from cart items.</p>
      </Field>

      <div className="border-t border-n2 pt-4 mt-6 mb-4">
        <h3 className="text-sm font-normal text-carbon-black mb-3">Shipment and delivery</h3>
      </div>

      <Field label="Delivery option" required>
        <select
          value={form.deliveryOption}
          onChange={e => setField('deliveryOption', e.target.value)}
          className="w-full border border-n3 px-3 py-2.5 text-sm focus:ring-1 focus:ring-electric-blue focus:border-electric-blue"
        >
          {DELIVERY_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </Field>

      <Field label="Requested delivery date">
        <input
          type="date"
          value={form.requestedDate}
          min={getTodayPlus(1)}
          onChange={e => setField('requestedDate', e.target.value)}
          className="w-60 border border-n3 px-3 py-2.5 text-sm focus:ring-1 focus:ring-electric-blue focus:border-electric-blue"
        />
      </Field>

      <Field label="Order criticality" required>
        <select
          value={form.criticality}
          onChange={e => setField('criticality', e.target.value)}
          className="w-full border border-n3 px-3 py-2.5 text-sm focus:ring-1 focus:ring-electric-blue focus:border-electric-blue"
        >
          {CRITICALITY_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </Field>
    </div>
  );
}

// ── Step 2: Billing Information ───────────────────────────────────

function BillingStep({ form, setField, poError }) {
  return (
    <div>
      <h2 className="text-lg font-sandvik-headline text-carbon-black mb-4">
        Billing information
      </h2>

      <div className="border border-n2 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="material-symbols-outlined text-sm text-n4">radio_button_checked</span>
          <span className="text-sm font-normal text-carbon-black">Reference number</span>
        </div>

        <Field label="Enter reference number / PO number" required error={poError}>
          <input
            type="text"
            value={form.poReference}
            onChange={e => setField('poReference', e.target.value)}
            className={`w-full border px-3 py-2.5 text-sm focus:ring-1 focus:ring-electric-blue focus:border-electric-blue
              ${poError ? 'border-red-500' : 'border-n3'}`}
          />
        </Field>

        <Field label="Enter any identification text for your reference (optional)">
          <input
            type="text"
            value={form.identificationText}
            onChange={e => setField('identificationText', e.target.value)}
            className="w-full border border-n3 px-3 py-2.5 text-sm focus:ring-1 focus:ring-electric-blue focus:border-electric-blue"
          />
        </Field>

        <Field label="Billing address">
          <select
            value={form.billingAddressId}
            onChange={e => setField('billingAddressId', e.target.value)}
            className="w-full border border-n3 px-3 py-2.5 text-sm focus:ring-1 focus:ring-electric-blue focus:border-electric-blue"
          >
            {DEMO_CUSTOMER.billingAddresses.map(addr => (
              <option key={addr.id} value={addr.id}>{addr.label}</option>
            ))}
          </select>
        </Field>
      </div>
    </div>
  );
}

// ── Step 3: Summary ───────────────────────────────────────────────

function SummaryStep({ form, cartItems, cartTotal, totalWeight, tcAccepted, setTcAccepted, onOpenTC }) {
  const selectedShipAddr = DEMO_CUSTOMER.shippingAddresses.find(a => a.id === form.shipToId);
  const selectedBillAddr = DEMO_CUSTOMER.billingAddresses.find(a => a.id === form.billingAddressId);

  // Estimated costs
  const additionalCost = cartItems.reduce((sum, item) => {
    if (item.bundledQty && item.bundledQty > 0 && item.quantity % item.bundledQty !== 0) {
      return sum + (item.shippingCost || 0);
    }
    return sum;
  }, 0);
  const grandTotal = cartTotal + additionalCost;

  return (
    <div>
      <h2 className="text-lg font-sandvik-headline text-carbon-black mb-6">
        Summary
      </h2>

      {/* Delivery and Invoice Address side-by-side */}
      <div className="border border-n2 p-6 mb-6">
        <h3 className="text-base font-normal text-carbon-black mb-4">
          Delivery and Invoice Address
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-n2 p-4">
            <p className="text-sm font-normal text-carbon-black mb-2">Delivery address</p>
            {selectedShipAddr ? (
              <div className="text-sm text-n6">
                <p>{selectedShipAddr.street || selectedShipAddr.label}</p>
                {selectedShipAddr.postalCode && <p>{selectedShipAddr.postalCode}</p>}
                {selectedShipAddr.province && selectedShipAddr.country && (
                  <p>{selectedShipAddr.province} {selectedShipAddr.country}</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-n5">—</p>
            )}
          </div>
          <div className="border border-n2 p-4">
            <p className="text-sm font-normal text-carbon-black mb-2">Invoice Address</p>
            <div className="text-sm text-n6">
              <p>{selectedBillAddr?.label || '—'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Item details */}
      <div className="border border-n2 mb-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-n2">
                <th className="text-left px-4 py-3 font-normal text-carbon-black">Item details</th>
                <th className="text-right px-4 py-3 font-normal text-carbon-black">Ordered quantity</th>
                <th className="text-right px-4 py-3 font-normal text-carbon-black">Total</th>
              </tr>
            </thead>
            <tbody>
              {cartItems.map(item => (
                <tr key={item.productId} className="border-b border-n1">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-4">
                      {item.thumbnailUrl || item.imageUrl ? (
                        <img
                          src={item.thumbnailUrl || item.imageUrl}
                          alt={item.description || item.itemCode}
                          className="w-20 h-16 object-contain bg-n1 flex-shrink-0"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-20 h-16 bg-n1 flex items-center justify-center flex-shrink-0">
                          <span className="material-symbols-outlined text-2xl text-n3">image</span>
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-normal text-carbon-black">{item.description}</p>
                        <p className="text-xs text-n5 mt-0.5">{item.itemCode}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-right text-carbon-black">{item.quantity}</td>
                  <td className="px-4 py-4 text-right font-normal text-carbon-black">
                    {formatCurrency(item.unitPrice * item.quantity)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Checkout summary */}
      <div className="border border-n2 p-6 mb-6">
        <h3 className="text-base font-normal text-carbon-black mb-4">Checkout summary</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-n5">Sub total:</span>
            <span className="text-carbon-black">{formatCurrency(cartTotal)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-n5">Estimated additional cost:</span>
            <span className="text-carbon-black">{formatCurrency(additionalCost)}</span>
          </div>
          <div className="flex justify-between border-t border-n2 pt-2 mt-2">
            <span className="text-n5">Total:</span>
            <span className="font-normal text-carbon-black text-base">{formatCurrency(grandTotal)}</span>
          </div>
        </div>
      </div>

      {/* T&C checkbox */}
      <div className="mb-2">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={tcAccepted}
            onChange={e => setTcAccepted(e.target.checked)}
            className="mt-0.5 w-4 h-4 border-n3 text-electric-blue focus:ring-electric-blue"
          />
          <span className="text-sm text-carbon-black">
            I agree to the{' '}
            <button
              type="button"
              onClick={onOpenTC}
              className="text-electric-blue hover:underline"
            >
              Terms and Conditions
            </button>
          </span>
        </label>
        {!tcAccepted && (
          <p className="text-xs text-red-500 ml-6 mt-1">
            *Please tick the terms and conditions before submitting the order
          </p>
        )}
      </div>
    </div>
  );
}

// ── Order Confirmation Page ──────────────────────────────────────

function OrderConfirmation({ poReference }) {
  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-sandvik-headline text-carbon-black mb-4">
        Thanks for shopping with us!
      </h1>
      <p className="text-base text-carbon-black mb-8">
        Your reference number : {poReference}
      </p>
      <div className="flex gap-4">
        <Link
          to="/products"
          className="px-6 py-2.5 bg-n2 text-sm text-carbon-black hover:bg-n3 transition-colors"
        >
          Continue shopping
        </Link>
        <Link
          to="/orders"
          className="px-6 py-2.5 bg-electric-blue text-white text-sm hover:bg-eb-3 transition-colors"
        >
          View my order
        </Link>
      </div>
    </div>
  );
}

// ── Main CheckoutWizard ───────────────────────────────────────────

const STEPS = ['Shipping Information', 'Billing Information', 'Summary'];

export default function CheckoutWizard() {
  const { state, dispatch } = useCart();
  const { account } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [toast, setToast] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [poError, setPOError] = useState('');
  const [tcAccepted, setTcAccepted] = useState(false);
  const [showTC, setShowTC] = useState(false);

  const userName = splitName(account?.name || '');
  const userPhone = account?.account?.phone || '';
  const customerId = account?.account?.customerId || '';

  const [form, setForm] = useState({
    contactFirstName: userName.first,
    contactLastName: userName.last,
    contactPhone: userPhone,
    shipToId: DEMO_CUSTOMER.shippingAddresses.find(a => a.isDefault)?.id || '',
    deliveryOption: '30',
    requestedDate: getTodayPlus(14),
    criticality: '10',
    poReference: '',
    identificationText: '',
    billingAddressId: DEMO_CUSTOMER.billingAddresses[0]?.id || '',
  });

  const setField = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'poReference') setPOError('');
  }, []);

  const cartTotal = useMemo(
    () => state.items.reduce((sum, item) => sum + (item.unitPrice || 0) * item.quantity, 0),
    [state.items]
  );

  const totalWeight = useMemo(
    () => state.items.reduce((sum, item) => sum + (item.weightLb || 0) * item.quantity, 0),
    [state.items]
  );

  const canProceed = useMemo(() => {
    if (currentStep === 0) {
      return !!form.shipToId && !!form.deliveryOption && !!form.criticality;
    }
    if (currentStep === 1) {
      return !!form.poReference.trim() && !poError;
    }
    return true;
  }, [currentStep, form, poError]);

  const handleNext = () => {
    if (currentStep === 1) {
      const po = form.poReference.trim();
      if (!po) {
        setPOError('Reference number / PO number is required.');
        return;
      }
      if (!isPOUnique(po, customerId)) {
        setPOError('This reference number has already been used for this account. Please enter a unique PO number.');
        return;
      }
    }
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    if (!tcAccepted) return;
    setSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));

    dispatch({ type: 'CLEAR_CART' });
    setSubmitted(true);
    setSubmitting(false);
    setToast({ type: 'success', message: 'Thanks for submitting your order' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Show confirmation page after submit
  if (submitted) {
    return (
      <>
        <OrderConfirmation poReference={form.poReference} />
        {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
      </>
    );
  }

  // Redirect if cart is empty
  if (state.items.length === 0 && !submitting) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <span className="material-symbols-outlined text-5xl text-n3 mb-3 block">shopping_cart</span>
        <p className="text-n5 font-sandvik-text mb-4">Your cart is empty. Add items before checking out.</p>
        <button
          onClick={() => navigate('/cart')}
          className="px-5 py-2.5 bg-electric-blue text-white text-sm hover:bg-eb-3 transition-colors"
        >
          Go to cart
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <StepIndicator currentStep={currentStep} steps={STEPS} />

      <div className="bg-white border border-n2 p-6 mb-6">
        {currentStep === 0 && (
          <ShippingStep form={form} setField={setField} totalWeight={totalWeight} />
        )}
        {currentStep === 1 && (
          <BillingStep form={form} setField={setField} poError={poError} />
        )}
        {currentStep === 2 && (
          <SummaryStep
            form={form}
            cartItems={state.items}
            cartTotal={cartTotal}
            totalWeight={totalWeight}
            tcAccepted={tcAccepted}
            setTcAccepted={setTcAccepted}
            onOpenTC={() => setShowTC(true)}
          />
        )}
      </div>

      {/* Navigation buttons */}
      <div className="flex justify-between">
        <div>
          {currentStep > 0 && (
            <button
              onClick={handleBack}
              className="px-5 py-2.5 bg-n2 text-sm text-carbon-black hover:bg-n3 transition-colors"
            >
              Previous
            </button>
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => navigate('/cart')}
            className="px-5 py-2.5 text-sm text-n5 hover:text-carbon-black transition-colors"
          >
            Cancel checkout
          </button>

          {currentStep < STEPS.length - 1 ? (
            <button
              onClick={handleNext}
              disabled={!canProceed}
              className={`px-5 py-2.5 text-sm text-white transition-colors
                ${canProceed
                  ? 'bg-electric-blue hover:bg-eb-3'
                  : 'bg-n3 cursor-not-allowed'}`}
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting || !tcAccepted}
              className={`px-6 py-2.5 text-sm text-white transition-colors flex items-center gap-2
                ${submitting || !tcAccepted
                  ? 'bg-n3 cursor-not-allowed'
                  : 'bg-electric-blue hover:bg-eb-3'}`}
            >
              {submitting ? (
                <>
                  <span className="material-symbols-outlined text-base animate-spin">progress_activity</span>
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </button>
          )}
        </div>
      </div>

      {/* T&C Modal */}
      {showTC && <TCModal onClose={() => setShowTC(false)} />}

      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
    </div>
  );
}
